import { useState } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { useSuspenseQuery } from '@tanstack/react-query'
import { outletsQuery } from '../../../lib/queries'
import { kg, short } from '../../../lib/format'
import { OutletForm } from '../../../components/outlet-forms'
import { Button, Card, Empty, Icon, Modal, PageHeader, cx } from '../../../components/ui'

export const Route = createFileRoute('/_app/outlets/')({
  loader: ({ context }) => context.queryClient.ensureQueryData(outletsQuery),
  component: Outlets,
})

function Outlets() {
  const { data } = useSuspenseQuery(outletsQuery)
  const [adding, setAdding] = useState(false)
  const byState = data.outlets.reduce<Record<string, typeof data.outlets>>((acc, o) => ((acc[o.state] ??= []).push(o), acc), {})

  return (
    <div>
      <PageHeader title="Outlets" subtitle="Stations, their tanks and how much gas is left."
        actions={<Button onClick={() => setAdding(true)}><Icon name="add" className="size-4" />Add outlet</Button>} />
      {!data.outlets.length && <Card className="mt-6"><Empty title="No outlets yet">Add your first outlet, then its tanks, to start supplying customers.</Empty></Card>}
      {Object.entries(byState).map(([state, outlets]) => (
        <section key={state} className="mb-8">
          <h2 className="mb-3 text-sm font-semibold text-ink-2">{state}</h2>
          <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 min-[1900px]:grid-cols-5">
            {outlets.map((o) => {
              const f = o.capacityKg ? o.levelKg / o.capacityKg : 0
              return (
                <li key={o.id}>
                  <Link to="/outlets/$outletId" params={{ outletId: o.id }} className="block rounded-card bg-card p-5 transition-shadow hover:shadow-card">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate font-semibold">{o.name}</p>
                        <p className="truncate text-xs text-muted">{o.region} · {o.address}</p>
                      </div>
                      <Icon name="nav-outlets" className="size-6 text-brand" />
                    </div>
                    <div className="mt-5 flex items-end justify-between text-xs">
                      <span><span className="num text-lg font-semibold">{short(o.levelKg)}</span> / {kg(o.capacityKg)}</span>
                      <span className="text-muted">{o.tanks} tanks · {o.customers} customers</span>
                    </div>
                    <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-black/5">
                      <div className={cx('h-full rounded-full', f > 0.3 ? 'bg-good-2' : 'bg-brand')} style={{ width: `${f * 100}%` }} />
                    </div>
                  </Link>
                </li>
              )
            })}
          </ul>
        </section>
      ))}
      <Modal open={adding} onClose={() => setAdding(false)} title="Add outlet">
        <OutletForm onDone={() => setAdding(false)} />
      </Modal>
    </div>
  )
}
